import { NgIf, AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import {
  TableColumn,
  TableColumnAction,
  TableColumnTypes,
  TableConfig,
  TableComponent,
} from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { COMPARISON_QUICKSIMULATION } from 'src/assets/data/feedProfitConstants';

import { Calculation, GenderOptions, SimulationType } from '../../models/feed-profit.model';
import * as actions from '../../store/feed-profit.actions';
import { getCalculations } from '../../store/feed-profit.selectors';

@Component({
  selector: 'app-feed-profit-overview',
  templateUrl: './feed-profit-overview.component.html',
  styleUrls: ['./feed-profit-overview.component.scss'],
  imports: [PageToolbarComponent, NgIf, TableComponent, AsyncPipe, TranslatePipe],
})
export class FeedProfitOverviewComponent implements OnInit {
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  columns: TableColumn[] = undefined;

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: this.columns,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };

  public tableConfig$: Observable<TableConfig>;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly router: Router,
  ) {}
  ngOnInit(): void {
    this.tableConfig$ = this.store.select(getCalculations).pipe(
      skipWhile(
        (calculations: Calculation[]) => calculations === null || calculations === undefined,
      ),
      map((calculations) => {
        const tableConfig: TableConfig = {
          ...this.tableConfigBase,
        };
        tableConfig.columns = <TableColumn[]>[
          {
            id: 'name',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-profit.overview.table.name'),
          },
          {
            id: 'createdDate',
            type: TableColumnTypes.DATE,
            label: this.localeService.translate('feed-profit.overview.table.created-date'),
          },
          {
            id: 'createdBy',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-profit.overview.table.created-by'),
          },
          {
            id: 'gender',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-profit.overview.table.genders'),
          },
          {
            id: 'country',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-profit.overview.table.country'),
          },
          {
            id: 'simulationType',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('feed-profit.overview.table.simulation-type'),
          },
        ];
        tableConfig.actions = <TableColumnAction[]>[
          {
            id: 'edit',
            callback: (row: Calculation) => this.editFeedProfitCalculation(row.id),
            label: this.localeService.translate('feed-profit.overview.table.edit'),
            icon: 'edit',
            disabled: () => false,
            primary: false,
          },
          {
            id: 'delete',
            callback: (row: Calculation) => this.deleteFeedProfitCalculation(row.id),
            label: this.localeService.translate('feed-profit.overview.table.delete'),
            icon: 'delete',
            disabled: () => false,
            primary: false,
          },
        ];

        if (calculations) {
          tableConfig.dataSource = new MatTableDataSource(
            this.createCalculationsDatasource(calculations),
          );
        } else {
          tableConfig.dataSource = new MatTableDataSource([]);
        }
        return tableConfig;
      }),
    );
  }

  private createCalculationsDatasource(calculations: Calculation[]) {
    const getSimulationType = (calculation: Calculation) => {
      const hasQuickSimulations = calculation.quickSimulations.length > 0;
      const hasExtendedSimulation =
        calculation.extendedSimulation &&
        Object.keys(calculation.extendedSimulation) &&
        Object.getPrototypeOf(calculation.extendedSimulation) === Object.prototype;

      if (hasQuickSimulations && hasExtendedSimulation) {
        return this.localeService.translate(`feed-profit.simulation-type.${SimulationType.BOTH}`);
      } else if (hasQuickSimulations && !hasExtendedSimulation) {
        return this.localeService.translate(
          `feed-profit.simulation-type.${SimulationType.QUICK_SIMULATION}`,
        );
      } else if (!hasQuickSimulations && hasExtendedSimulation) {
        return this.localeService.translate(
          `feed-profit.simulation-type.${SimulationType.EXTENDED_SIMULATION}`,
        );
      }
      return this.localeService.translate('feed-profit.simulation-type.no-simulations-found');
    };

    return calculations.map((calculation: Calculation) => ({
      id: calculation.id,
      name: calculation.name,
      createdDate: calculation.createdAt ?? new Date().toISOString(),
      createdBy: calculation.createdBy,
      gender: calculation.genders
        ? this.localeService.translate(`feed-profit.genders.${calculation.genders}`)
        : this.localeService.translate(`feed-profit.genders.${GenderOptions.BOAR_GILT}`),
      country: calculation.country
        ? this.localeService.translate(`feed-profit.country.${calculation.country}`)
        : '',
      simulationType: getSimulationType(calculation),
      extendedBaseline: {},
      comparisonSimulation: calculation.comparisonSimulation ?? COMPARISON_QUICKSIMULATION,
      feeders: [] as string[],
    }));
  }

  createFeedProfitCalculation() {
    const newCalculation = <Calculation>{
      createdAt: new Date().toISOString(),
    };
    this.store.dispatch(
      actions.updateSelectedCalculation({
        values: {
          ...newCalculation,
        },
      }),
    );
    this.router.navigate(['/feed-profit', 'create']).then();
  }

  editFeedProfitCalculation(calculationId: number) {
    this.store.dispatch(actions.navigationCalculationDetail({ calculationId }));
  }

  deleteFeedProfitCalculation(calculationId: number) {
    this.store.dispatch(actions.deleteCalculationWithConfirmation({ calculationId }));
  }
}
